import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Chart,
    Chart as ChartJS,
    Tooltip, Legend,
} from 'chart.js';
import moment from 'moment';
import {useMutation} from '@tanstack/react-query';
import {getRebateReportByPeriod} from '../library/api';


Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale);
ChartJS.register(
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
);
const PeriodReport = () => {
    const [dateModalOpen, setDateModalOpen] = useState(false);
    const chartRef = useRef(null);
    const [year, setYear] = useState(moment(new Date()).format('YYYY'));
    const [month, setMonth] = useState(new Date());
    const [yearOptions, setYearOptions] = useState([]);

    // 기간별 리포트 (박스안 녀용)
    const {mutate: getByPeriodMutate, data: getByPeriodData} = useMutation({
        mutationFn: getRebateReportByPeriod,
        mutationKey: ['getRebateReportByPeriod'],
        onSuccess: data => {
            if (data.year.length > 0) {
                setYearOptions(data.year.map(item => ({value: item, label: item})));
            } else {
                setYearOptions([{value: moment(new Date()).format('YYYY'), label: moment(new Date()).format('YYYY')}]);
            }
        },
        onError: error => {
            if (error.response.data.code !== 401) {
                alert(error.response.data.message);
            }
            throw error;
        }
    });


    useEffect(() => {
        if (!getByPeriodData) return;
        const graphValue = getByPeriodData?.monthlyPayAmt?.map((e, i) => {
            return moment(`${year}-${i + 1}`).format('YYYY-MM') >= moment(new Date()).format('YYYY-MM') ? null : e.payAmt;
        });

        const graphDate = getByPeriodData?.monthlyPayAmt?.map((e, i) => {
            return `${year}.${(i + 1).toString().padStart(2, '0')}`;
        });
        const graphMax = graphValue && Math.ceil(Math.max(...graphValue) / 10000) * 10000;

        const ctx = chartRef.current.getContext('2d');
        const chart = new ChartJS(ctx, {
            type: 'line',
            data: {
                labels: graphDate,
                datasets: [
                    {
                        label: '수익금액',
                        data: graphValue,
                        borderColor: 'rgb(20, 184, 166)',
                        backgroundColor: 'rgb(20, 184, 166)',
                        tension: 0.1,
                        pointRadius: 4,
                        borderWidth: 1,
                        pointBackgroundColor: 'rgb(20, 184, 166)',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        max: graphMax === 0 ? 10000 : graphMax,
                        ticks: {
                            stepSize: (graphMax === 0 ? 10000 : graphMax) / 5,
                            color: 'rgb(20, 184, 166)',
                            font: {
                                size: 12,
                            },
                            callback: function (value) {
                                return value.toLocaleString();
                            },
                        },
                        grid: {
                            color: 'rgb(229, 231, 235)',
                            drawBorder: false,
                        },
                        border: {
                            display: false,
                        },
                    },
                    x: {
                        grid: {
                            display: false,
                            drawBorder: false,
                        },
                        ticks: {
                            color: 'rgb(156, 163, 175)',
                            font: {
                                size: 12,
                            },
                            padding: 10,
                        },
                        offset: true,
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true, // 툴팁을 활성화
                        backgroundColor: 'rgba(0, 0, 0, 0.8)', // 툴팁 배경색
                        titleFont: {
                            size: 14,
                            weight: 'bold',
                        },
                        bodyFont: {
                            size: 12,
                        },
                        padding: 10,
                        callbacks: {
                            label: function (tooltipItem) {
                                return tooltipItem.dataset.label + ': ' + tooltipItem.formattedValue;
                            }
                        }
                    },
                },

                layout: {
                    padding: {
                        left: 10,
                        right: 10,
                        top: 20,
                        bottom: 20,
                    },
                },
            },
            plugins: [
                {
                    beforeDraw: function (chart) {
                        const ctx = chart.ctx;
                        const xAxis = chart.scales.x;
                        const yAxis = chart.scales.y;

                        ctx.save();
                        ctx.beginPath();
                        ctx.moveTo(xAxis.left, yAxis.bottom);
                        ctx.lineTo(xAxis.right, yAxis.bottom);
                        ctx.lineWidth = 2;
                        ctx.strokeStyle = 'rgb(156, 163, 175)';
                        ctx.stroke();
                        ctx.restore();
                    },
                },
            ],
        });

        // Cleanup on component unmount
        return () => {
            chart.destroy();
        };
    }, [getByPeriodData]);


    useEffect(() => {
        const items = {
            rebateDt: null,
            startDt: year + '-01-01',
            endDt: year + '-12-31',
        };
        getByPeriodMutate(items);

    }, [year]);
    const handleChange = useCallback((event) => {
        setYear(event.target.value);
    }, [year]);
    return (
        <>
            <main className="flex-grow container mx-auto px-4 py-16">
                <div className="max-w-4xl mx-auto">
                    <div className="flex justify-between sm:flex-row flex-col items-center mb-8">
                        <h1 className="sm:text-3xl text-2xl font-bold">기간별 리포트 <span
                            className="text-teal-500 text-lg font-medium ml-2">{year}</span>
                        </h1>
                        <div className="text-sm font-medium flex items-center gap-[10px] sm:mt-0 mt-4">
                            날짜 범위 <select className="border rounded-[10px] h-9 px-2 pr-14 text-sm cursor-pointer"
                                          value={year}
                                          onChange={handleChange}>
                            {
                                yearOptions && yearOptions?.map(item => (
                                    <option value={item.value} key={item.value}>{item.label}</option>
                                ))
                            }
                        </select>

                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-300 p-6 mb-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <p className="text-sm text-gray-600 mb-1">고객수</p>
                                <p className="text-xl sm:text-2xl font-medium">{getByPeriodData?.total?.corpCnt && (getByPeriodData?.total?.corpCnt).toLocaleString()}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-sm text-gray-600 mb-1">일사 사용자 수(명)</p>
                                <p className="text-xl sm:text-2xl font-medium">{getByPeriodData?.total?.userCnt && (getByPeriodData?.total?.userCnt).toLocaleString()}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-sm text-gray-600 mb-1">일사 수수료 금액(원)</p>
                                <p className="text-xl sm:text-2xl font-medium">{getByPeriodData?.total?.feeAmt && (getByPeriodData?.total?.feeAmt).toLocaleString()}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-sm text-gray-600 mb-1">파트너스 수익금액(원)</p>
                                <p className="text-xl sm:text-2xl font-medium text-teal-500">{getByPeriodData?.total?.payAmt && (getByPeriodData?.total?.payAmt).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <p className="text-xs text-gray-500 mb-8">* 위 수익금액은 조회일 현재 기준 예상 금액으로 월말 정산 시 변경될 수 있습니다.</p>


                    <div className="relative h-[400px]">
                        {/*<ChartComponent chartDate={chartDate}*/}
                        {/*                chartMonth={chartMonth}></ChartComponent>*/}
                        <canvas id="revenueChart" ref={chartRef}></canvas>
                    </div>
                </div>
            </main>
            {
                dateModalOpen && <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white sm:p-10 p-4 rounded-lg shadow-lg">
                        <h2 className="text-xl font-bold mb-8">날짜 범위</h2>
                        <div className="flex items-center gap-x-4">
                            <label className="flex items-center custom-radio">
                                <input type="radio" name="dateRange" value="yesterday"/>
                                <span className="radio-button"></span>
                                <span className="ml-2 text-sm">어제</span>
                            </label>
                            <label className="flex items-center custom-radio">
                                <input type="radio" name="dateRange" value="week"/>
                                <span className="radio-button"></span>
                                <span className="ml-2 text-sm">일주일</span>
                            </label>
                            <label className="flex items-center custom-radio">
                                <input type="radio" name="dateRange" value="30days"/>
                                <span className="radio-button"></span>
                                <span className="ml-2 text-sm">30일</span>
                            </label>
                            <label className="flex items-center custom-radio">
                                <input type="radio" name="dateRange" value="custom"/>
                                <span className="radio-button"></span>
                                <span className="ml-2 text-sm">맞춤형</span>
                            </label>
                        </div>
                        <div className="mt-8 flex space-x-4">
                            <input type="date" className="sm:w-[200PX] border rounded-[10px] px-4 py-1 outline-none"
                                   value="2024-09-12"/>
                            <span className="self-center">-</span>
                            <input type="date" className="sm:w-[200PX] border rounded-[10px] px-4 py-1 outline-none"
                                   value="2024-09-12"/>
                        </div>
                        <p className="text-sm mt-5">* 최대 90일 범위의 리포트만 제공됩니다.</p>
                        <div className="mt-8 flex justify-center space-x-4">
                            <button onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setDateModalOpen(false);
                            }}
                                    className="px-16 py-[10px] border border-gray-400 rounded-[24px]">취소
                            </button>
                            <button onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setDateModalOpen(false);
                            }}
                                    className="px-16 py-[10px] bg-teal-500 text-white rounded-[24px]">승인
                            </button>
                        </div>
                    </div>
                </div>
            }
        </>
    );
};

export default PeriodReport;
